export const scienceCourse = [
    {
        "metadata": {
            "id": "science_y7_week1a",
            "classTitle": "Science",
            "keyStage": 3,
            "week": 1,
            "order": 1,
            "module": "Working Scientifically",
            "lesson": "Week 1A",
            "yearGroup": "Year 7",
            "title": "What Is Science? Asking Questions",
            "subheading": "Working Scientifically",
            "description": "Students explore what makes a question scientific and how curiosity drives science.",
            "curriculumAlignment": "KS3 Science – Scientific attitudes"
        },
        "slides": [
            {
                "title": "🔍 Welcome to Science!",
                "content": "Science starts with QUESTIONS.\nWhat do YOU want to know about the world?",
                "image": "curious_students_questioning.jpg",
                "speakerNotes": {
                    "objective": "Spark excitement and curiosity about science. Make it personal.",
                    "questions": ["What’s the weirdest question you’ve ever asked?"],
                    "imagePrompt": "Students looking at a glowing globe with questioning expressions."
                }
            },
            {
                "title": "🧪 What Do Scientists Actually Do?",
                "content": "Scientists observe, question, test, and explain.\nThey are professional wonderers!",
                "image": "scientist_team_observing.jpg",
                "speakerNotes": {
                    "objective": "Introduce science as a process, not just facts.",
                    "questions": ["Can anyone be a scientist?", "Do scientists always agree?"]
                }
            },
            {
                "title": "👁️ See–Think–Wonder",
                "content": "Look at this image...\n🔍 What do you see?\n💭 What do you think is happening?\n❓ What do you wonder?",
                "image": "mysterious_nature_image.jpg",
                "speakerNotes": {
                    "objective": "Scaffold students to generate natural science questions from observation.",
                    "imagePrompt": "A strange plant in foggy weather or a chemical reaction bubbling.",
                    "questions": ["What stands out to you?", "Would you want to investigate this?"]
                }
            },
            {
                "title": "❓ What Makes a Scientific Question?",
                "content": "A good science question is:\n✔️ Testable\n✔️ Based on evidence\n✔️ Open-ended\n✔️ Focused on cause/effect",
                "image": "question_sort_activity.jpg",
                "speakerNotes": {
                    "objective": "Clarify what distinguishes a scientific question from others.",
                    "questions": ["Can we test this question in real life?"],
                    "imagePrompt": "Two categories: Scientific vs. Not Scientific"
                }
            },
            {
                "title": "🤖 Let’s Ask AI a Question",
                "content": "Prompt: 'What science questions can I ask about this volcano scene?'\n\nWhat did the AI generate?",
                "image": "ai_question_generator.jpg",
                "speakerNotes": {
                    "objective": "Model how AI can assist in question generation.",
                    "aiPrompt": "Generate 3 scientific questions about a volcano eruption image."
                }
            },
            {
                "title": "🧠 Generate and Improve a Question",
                "content": "Write your own science question.\nSwap it with a partner.\nUse AI or peer suggestions to improve it!",
                "image": "students_question_swap.jpg",
                "speakerNotes": {
                    "objective": "Peer review and iteration of scientific questions.",
                    "questions": ["Is this question too broad or too vague?"],
                    "imagePrompt": "Students exchanging and annotating each other's post-it notes."
                }
            },
            {
                "title": "🧪 Build Your Mini-Investigation",
                "content": "Pick one good question.\nNow sketch how you could test it:\n- What would you observe?\n- What tools would you need?",
                "image": "simple_experiment_design.jpg",
                "speakerNotes": {
                    "objective": "Begin linking questions to investigations.",
                    "questions": ["Would your experiment be fair and safe?"]
                }
            },
            {
                "title": "🎨 Gallery Walk: Our Questions",
                "content": "Post your best question around the room.\nWalk around and vote: Which is most curious?\nWhich is most scientific?",
                "image": "classroom_question_wall.jpg",
                "speakerNotes": {
                    "objective": "Celebrate ideas and reinforce what makes a question strong.",
                    "questions": ["What makes that question stand out?"]
                }
            },
            {
                "title": "🧠 Metacognition Moment",
                "content": "Think about your thinking:\n- What makes YOU curious?\n- How did you come up with your question?\n- Did AI change how you thought today?",
                "image": "student_thinking_clouds.jpg",
                "speakerNotes": {
                    "objective": "Prompt self-awareness and reflection.",
                    "imagePrompt": "A thought bubble with a brain and lightbulb."
                }
            },
            {
                "title": "🌟 Exit Task: Big Question of the Day",
                "content": "Write your BIG science question in your AIDEN Journal.\nRate your curiosity from 1–5.\nBe ready to share tomorrow!",
                "image": "student_writing_journal.jpg",
                "speakerNotes": {
                    "objective": "Close the loop: encourage continued inquiry beyond today.",
                    "questions": ["How do we know which questions are worth investigating?"]
                }
            }
        ],
        "teacherMaterials": [
            {
                "name": "Lesson Plan & Objectives",
                "componentType": "lessonplanObjectives",
                "content": [
                    {
                        "sectionName": "Learning Objectives",
                        "title": "What Students Will Learn",
                        "description": "By the end of this lesson, students will:\n✅ Understand what makes a question scientific\n✅ Use AI to generate and improve questions\n✅ Design a basic plan to test a question\n✅ Reflect on their own curiosity and inquiry habits",
                        "icon": "TargetIcon"
                    }
                ]
            },
            {
                "name": "Assessment & Homework",
                "componentType": "assessment",
                "content": [
                    {
                        "sectionName": "Assessment Criteria",
                        "title": "Evaluating Questioning Skills",
                        "description": "✔️ Can distinguish scientific from non-scientific questions\n✔️ Can generate and revise a testable question\n✔️ Uses AI thoughtfully in ideation\n✔️ Reflects on their thinking process",
                        "icon": "CheckIcon"
                    },
                    {
                        "sectionName": "Homework Task",
                        "title": "Curiosity at Home",
                        "description": "Observe something unusual or interesting at home. Write one science question in your AIDEN Journal. Share it with a family member.",
                        "icon": "HomeIcon"
                    }
                ]
            },
            {
                "name": "Metacognitive Exercises",
                "componentType": "metacognitiveExercise",
                "content": [
                    {
                        "sectionName": "Reflection Prompts",
                        "title": "Thinking About Thinking",
                        "description": "🧠 What helped you come up with your question?\n🔍 Did you change your question after talking to someone or AI?\n📓 What makes YOU curious in science?\n✨ How could today’s thinking help you in other subjects?",
                        "icon": "BrainIcon"
                    }
                ]
            },
            {
                "name": "Materials Needed",
                "componentType": "materials",
                "content": [
                    {
                        "sectionName": "Teacher Prep List",
                        "title": "Materials for Today’s Class",
                        "description": "📝 Sticky notes (for student questions)\n📄 Question sorting cards (scientific vs not)\n📚 Visual prompt image (printed or projected)\n🖍️ Flip chart or whiteboard for group ideas\n📱 Access to AIDEN GPT or classroom AI device\n📓 AIDEN Student Journals\n💻 Optional: Free tools like Miro, Padlet, or Quick, Draw! for brainstorming or drawing inquiry scenes",
                        "icon": "ClipboardIcon"
                    }
                ]
            }
        ]
    },
    
    {
        "metadata": {
            "id": "science_y7_week1b",
            "classTitle": "Science",
            "keyStage": 3,
            "week": 1,
            "order": 2,
            "module": "Working Scientifically",
            "lesson": "Week 1B",
            "yearGroup": "Year 7",
            "title": "Planning a Scientific Investigation",
            "subheading": "Working Scientifically",
            "description": "Students learn how to structure a scientific investigation, define variables, and use AI tools to assist in method design.",
            "curriculumAlignment": "KS3 Science – Experimental skills and investigations"
        },
        "slides": [
            {
                "externalLink": "https://www.nasa.gov/mission_pages/station/research/news/plant-growth-space",
                "title": "Activate thinking around testable methods.",
                "content": "What would you change? What would you measure?",
                "imagePrompt": "Students raising hands and discussing with animated plant illustrations."
            },

            {
                "title": "🧪 What Is a Scientific Investigation?",
                "content": "A science investigation has:\n1. A clear question\n2. A prediction\n3. A fair method\n4. Measurable results\n🌍 Real-world link: Climate scientists test if clouds reflect heat to model future weather.",
                "image": "investigation_flowchart.jpg",
                "speakerNotes": {
                    "objective": "Introduce the basic stages of a practical investigation.",
                    "imagePrompt": "Flowchart diagram showing steps of an experiment."
                }
            },
            {
                "title": "🔍 Variables: Change, Measure, Keep Same",
                "content": "Independent Variable = What you change\nDependent Variable = What you measure\nControl Variables = What you keep the same\n🏎️ Real-world link: F1 engineers test tyre materials while controlling track and temperature.",
                "image": "variable_sort_cards.jpg",
                "speakerNotes": {
                    "objective": "Clarify key vocabulary for investigative science.",
                    "questions": ["What variable would you change in a balloon race?"],
                    "imagePrompt": "Color-coded cards or blocks labelled with variable types."
                }
            },
            {
                "title": "🎯 Practice: Sort the Variables!",
                "content": "Activity: You’ll get an example investigation.\nCan you identify the variables?\nUse your sorting cards!\n🧪 Real-world link: Medical researchers test vaccines by comparing immune responses (dependent variable) with and without a booster (independent variable).",
                "image": "students_sorting_cards.jpg",
                "speakerNotes": {
                    "objective": "Reinforce understanding of variables using examples.",
                    "imagePrompt": "Group of students using labelled cards on a table."
                }
            },
            {
                "title": "🤖 Let AI Design a Method",
                "content": "Prompt: 'Give me a method to test which material is best at keeping heat in.'\n\nDid it include variables? Fair test?\n🔥 Real-world link: Architects use thermal cameras to test insulation before designing energy-efficient buildings.",
                "image": "ai_lab_helper.jpg",
                "speakerNotes": {
                    "objective": "Demonstrate how AI can scaffold an investigation setup.",
                    "aiPrompt": "Generate a method to test which cup material is the best insulator.",
                    "imagePrompt": "Illustration of a digital assistant next to a heat lamp and materials."
                }
            },
            {
                "title": "🧠 Review AI’s Method",
                "content": "Did AI: \n✔️ Identify variables?\n✔️ Include a fair method?\n✔️ Suggest measurements?\n📱 Real-world link: App developers A/B test changes (like button colors) to measure click rates — same logic!",
                "image": "students_evaluating_ai.jpg",
                "speakerNotes": {
                    "objective": "Teach students to critically evaluate AI outputs using scientific criteria.",
                    "imagePrompt": "Students with checklists reviewing printed AI methods."
                }
            },
            {
                "title": "✍️ Build Your Own Investigation Plan",
                "content": "Use a question you created last lesson.\nPlan:\n- What will you change?\n- What will you measure?\n- How will it be fair?\n🛠️ Real-world link: Engineers test bridge materials for stress and safety using control tests.",
                "image": "experiment_planning_template.jpg",
                "speakerNotes": {
                    "objective": "Apply theory to student-generated questions.",
                    "imagePrompt": "Worksheet with labelled boxes for question, method, prediction."
                }
            },
            {
                "title": "📢 Share Your Plan!",
                "content": "Groups present their plans.\nGet feedback: Is it fair? Is it doable? Is it safe?\n👩‍🔬 Real-world link: Scientists present experiment proposals at conferences for peer review.",
                "image": "student_presentations.jpg",
                "speakerNotes": {
                    "objective": "Encourage verbal reasoning and collaborative critique.",
                    "imagePrompt": "Students presenting posters with bullet points and diagrams."
                }
            },
            {
                "title": "🧠 Metacognition Moment",
                "content": "What was the hardest part?\nHow did you fix it?\nDid AI help you think differently?\n🌍 Real-world link: Even professional scientists revisit their methods to improve accuracy.",
                "image": "students_thinking_together.jpg",
                "speakerNotes": {
                    "objective": "Encourage reflection on problem-solving and planning processes.",
                    "imagePrompt": "Thought bubbles above students with 'challenge' and 'solution'."
                }
            },
            {
                "title": "📝 Exit Task: Investigation Summary",
                "content": "Summarise your plan in 3 sentences:\n1. My question is...\n2. I will change... and measure...\n3. I’ll keep it fair by...\n🌟 Real-world link: Lab reports often begin with a similar experiment summary to guide future research.",
                "image": "student_summary_cards.jpg",
                "speakerNotes": {
                    "objective": "Reinforce clear communication of investigation logic.",
                    "imagePrompt": "Mini whiteboards or index cards with student summaries."
                }
            }
        ],
        "teacherMaterials": [
            {
                "name": "Lesson Plan & Objectives",
                "componentType": "lessonplanObjectives",
                "content": [
                    {
                        "sectionName": "Learning Objectives",
                        "title": "What Students Will Learn",
                        "description": "By the end of this lesson, students will:\n✅ Understand the structure of a scientific investigation\n✅ Identify and classify variables\n✅ Use AI to assist in method design\n✅ Plan and explain their own experiment",
                        "icon": "TargetIcon"
                    }
                ]
            },
            {
                "name": "Assessment & Homework",
                "componentType": "assessment",
                "content": [
                    {
                        "sectionName": "Assessment Criteria",
                        "title": "Investigation Planning Skills",
                        "description": "✔️ Identifies independent, dependent, and control variables\n✔️ Outlines a fair and logical method\n✔️ Uses AI critically\n✔️ Explains the investigation using scientific vocabulary",
                        "icon": "CheckIcon"
                    },
                    {
                        "sectionName": "Homework Task",
                        "title": "Mini-Plan for a Home Test",
                        "description": "Create a 3-sentence plan for an experiment at home. Use an everyday question (e.g., Which kitchen sponge absorbs the most water?).",
                        "icon": "HomeIcon"
                    }
                ]
            },
            {
                "name": "Metacognitive Exercises",
                "componentType": "metacognitiveExercise",
                "content": [
                    {
                        "sectionName": "Reflection Prompts",
                        "title": "Thinking About Scientific Planning",
                        "description": "🔍 What part of planning was easiest or hardest?\n🤖 Did AI help or confuse you?\n🧠 What did you learn about how you solve problems?\n🧪 When might a scientist revisit their method in real life?",
                        "icon": "BrainIcon"
                    }
                ]
            },
            {
                "name": "Materials Needed",
                "componentType": "materials",
                "content": [
                    {
                        "sectionName": "Teacher Prep List",
                        "title": "Materials for Today’s Class",
                        "description": "📄 Variable sorting cards (independent/dependent/control)\n📋 Experiment planning worksheets or Google Docs\n🖍️ Sticky notes, markers, whiteboard\n📱 AIDEN GPT access or printed AI outputs\n📓 AIDEN Student Journals\n💻 Optional: Miro, Google Jamboard, Canva, or Tinkercad Science Journal",
                        "icon": "ClipboardIcon"
                    }
                ]
            }
        ]
    }
]
